import classify from '../../../../helpers/classify';
import { getFocusPoint } from './zoomerUtils';

interface RotatorConfig {
  startSlide?: number;
  interval?: number;
  cycle?: boolean;
  recurrent?: boolean;
  grabbingClass?: string;
  currentClass?: string;
  imagesClass?: string;
  onShow?: ({ current }: { current: number }) => void;
}

export interface RotatorInstance {
  handleStart: (e: Event) => void;
  handleMove: (e: Event) => void;
  handleEnd: (e: Event) => void;
  handlePlay: () => void;
  handleShow: (index: number) => void;
  handleStop: () => void;
}

function Rotator(container: HTMLElement, config: RotatorConfig = {}): RotatorInstance {
  const options = {
    startSlide: 0,
    interval: 50,
    cycle: true,
    recurrent: false,
    grabbingClass: 'rotator--grabbing',
    currentClass: 'rotator--current',
    imagesClass: 'image-set__image',
    onShow: (): void => {},
    ...config
  };

  const children = container.querySelectorAll(classify(options.imagesClass));
  const lengthOfChildren = children.length;

  let current = 0;
  let isRotating: number | null | undefined = null;

  let focusPoint: number | null | undefined = null;
  let touchedIndex: number | null | undefined = null;

  (function initialConfigs(): void {
    show(options.startSlide);
  })();

  function handlePlay(): void {
    const { startSlide, interval, recurrent } = options;
    function rotate(): void {
      if (current === lengthOfChildren - 1) {
        stop();
        show(startSlide);
        if (recurrent) {
          handlePlay();
        }
      } else {
        show(current + 1);
      }
    }
    isRotating = window.setInterval(rotate, interval);
  }

  function stop(): void {
    if (isRotating) {
      window.clearInterval(isRotating);
    }
    isRotating = undefined;
  }

  function show(n: number): void {
    const { cycle, currentClass, onShow } = options;
    const lastSlide = lengthOfChildren - 1;
    const getNextSlide = (nextSlide: number): number => {
      if (nextSlide < 0) {
        return cycle ? nextSlide + lengthOfChildren : 0;
      }
      if (nextSlide > lastSlide) {
        return cycle ? nextSlide - lengthOfChildren : lastSlide;
      }
      return nextSlide;
    };
    const next = getNextSlide(n);
    children[current].classList.remove(currentClass);
    children[next].classList.add(currentClass);
    current = next;
    onShow(getCurrentStatus());
  }

  function handleStart(event: Event): void {
    if (isRotating) {
      stop();
    }
    touchedIndex = current;
    focusPoint = getFocusPoint(event).x - container.offsetLeft;
    container.classList.add(options.grabbingClass);
  }

  function handleMove(event: Event): void {
    if (typeof touchedIndex !== 'number') return;
    const currentFocusPoint = getFocusPoint(event).x - container.offsetLeft;
    if (focusPoint) {
      const difference = currentFocusPoint - focusPoint;
      const offsetIndex = Math.floor((difference / container.clientWidth) * lengthOfChildren);
      show(touchedIndex + offsetIndex);
    }
  }

  function handleEnd(): void {
    container.classList.remove(options.grabbingClass);
    focusPoint = undefined;
    touchedIndex = undefined;
  }

  function handleStop(): void {
    if (isRotating) {
      stop();
    }
  }

  function handleShow(index: number): void {
    handleStop();
    show(index);
  }

  function getCurrentStatus(): { current: number } {
    return { current };
  }

  function proxyEvent(handler: (e: Event) => void): (e: Event) => void {
    return (event: Event): void => {
      if (event.type.includes('mouse') || event.type.includes('touchend')) {
        event.preventDefault();
      }
      handler(event);
    };
  }

  return {
    handleStart: proxyEvent(handleStart),
    handleMove: proxyEvent(handleMove),
    handleEnd: proxyEvent(handleEnd),
    handlePlay,
    handleShow,
    handleStop
  };
}

export default Rotator;
